/**
 * SprintForge - API Service
 * Centralized API client with authentication
 */

import axios, { AxiosInstance } from 'axios'
import { msalInstance } from '../auth/msalInstance'
import { apiScopes } from '../auth/authConfig'
import type {
  Project,
  CreateProjectRequest,
  UpdateProjectRequest,
  Document,
  DocumentCategory,
  ChatMessage,
  ChatRequest,
  ChatResponse,
  GapAnalysisResult,
  AnalysisType,
  AnalysisTypeInfo,
  AnalysisJobStartResponse,
  AnalysisJobStatusResponse,
  KickoffQuestionsResult,
  QuestionType,
  GeneratedOutput,
  OutputType,
  OutputTypeInfo,
  OutputJobStartResponse,
  OutputJobStatusResponse,
  SmartsheetWorkspace,
  SmartsheetSheet,
  SmartsheetConnectRequest,
  SmartsheetConnectionStatus,
  SmartsheetImportResult,
  PostImportTask,
  PostImportSummary,
  TeamsMeetingsResponse,
  TeamsTranscriptImportRequest,
  TeamsTranscriptImportResponse,
  TeamsTranscript,
} from '../types'

// =============================================================================
// API INSTANCE
// =============================================================================

const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  headers: { 'Content-Type': 'application/json' },
  timeout: 300000, // 5 minutes for long operations
})

// Get access token for API calls
async function getAccessToken(): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts()
  if (accounts.length === 0) return null

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiScopes,
      account: accounts[0],
    })
    return response.accessToken
  } catch (error) {
    console.error('Failed to acquire token silently:', error)
    // Try interactive login
    try {
      const response = await msalInstance.acquireTokenPopup(apiScopes)
      return response.accessToken
    } catch (popupError) {
      console.error('Failed to acquire token via popup:', popupError)
      return null
    }
  }
}

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  const token = await getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// =============================================================================
// PROJECTS
// =============================================================================

export const getProjects = (status?: string, customer_name?: string) =>
  api.get<{ projects: Project[]; total: number }>('/api/projects', {
    params: { status, customer_name },
  })

export const getProject = (projectId: string) => api.get<Project>(`/api/projects/${projectId}`)

export const createProject = (data: CreateProjectRequest) =>
  api.post<Project>('/api/projects', data)

export const updateProject = (projectId: string, data: UpdateProjectRequest) =>
  api.put<Project>(`/api/projects/${projectId}`, data)

export const deleteProject = (projectId: string) => api.delete(`/api/projects/${projectId}`)

// =============================================================================
// DOCUMENTS
// =============================================================================

export const getDocuments = (projectId: string, category?: DocumentCategory) =>
  api.get<{ documents: Document[]; total: number }>(`/api/projects/${projectId}/documents`, {
    params: { doc_category: category },
  })

export const getDocument = (projectId: string, documentId: string) =>
  api.get<Document>(`/api/projects/${projectId}/documents/${documentId}`)

export const uploadDocument = (
  projectId: string,
  file: File,
  category: DocumentCategory,
  subcategory?: string
) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('category', category)
  if (subcategory) formData.append('subcategory', subcategory)

  return api.post<Document>(`/api/projects/${projectId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const deleteDocument = (projectId: string, documentId: string) =>
  api.delete(`/api/projects/${projectId}/documents/${documentId}`)

export const reprocessDocument = (projectId: string, documentId: string) =>
  api.post<Document>(`/api/projects/${projectId}/documents/${documentId}/reprocess`)

// =============================================================================
// CHAT
// =============================================================================

export const sendChatMessage = (projectId: string, data: ChatRequest) =>
  api.post<ChatResponse>(`/api/projects/${projectId}/chat`, data)

export const getChatHistory = (projectId: string, limit?: number) =>
  api.get<{ messages: ChatMessage[] }>(`/api/projects/${projectId}/chat/history`, {
    params: { limit },
  })

export const getChatSuggestions = (projectId: string, count?: number) =>
  api.get<{ suggestions: string[] }>(`/api/projects/${projectId}/chat/suggestions`, {
    params: { count },
  })

export const clearChatHistory = (projectId: string) =>
  api.delete(`/api/projects/${projectId}/chat/history`)

// =============================================================================
// GAP ANALYSIS
// =============================================================================

export const runGapAnalysis = (
  projectId: string,
  analysisType: AnalysisType,
  docCategories?: DocumentCategory[]
) =>
  api.post<GapAnalysisResult>(`/api/projects/${projectId}/analysis/gaps`, null, {
    params: { analysis_type: analysisType, doc_categories: docCategories },
  })

export const runAllGapAnalyses = (projectId: string) =>
  api.post<AnalysisJobStartResponse>(`/api/projects/${projectId}/analysis/gaps/all`)

export const getAnalysisJobStatus = (projectId: string, jobId: string) =>
  api.get<AnalysisJobStatusResponse>(`/api/projects/${projectId}/analysis/status/${jobId}`)

export const getGapAnalysisResults = (projectId: string, analysisType?: AnalysisType) =>
  api.get<GapAnalysisResult[]>(`/api/projects/${projectId}/analysis/gaps`, {
    params: { analysis_type: analysisType },
  })

export const getAnalysisTypes = () => api.get<AnalysisTypeInfo[]>(`/api/projects/analysis/types`)

export const deleteGapAnalysis = (projectId: string, analysisId: string) =>
  api.delete<{ success: boolean; deleted_id: string }>(
    `/api/projects/${projectId}/analysis/gaps/${analysisId}`
  )

export const downloadGapAnalysisCSV = async (projectId: string, analysisType: AnalysisType) => {
  const response = await api.get(`/api/projects/${projectId}/analysis/gaps/${analysisType}/csv`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `gap_analysis_${analysisType}.csv`)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const downloadAllAnalysisZIP = async (projectId: string) => {
  const response = await api.get(`/api/projects/${projectId}/analysis/export/zip`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `analysis_export.zip`)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const getExecutiveSummary = (projectId: string) =>
  api.get<{ summary: string; format: string }>(`/api/projects/${projectId}/analysis/export/summary`)

export const downloadExecutiveSummary = async (projectId: string) => {
  const response = await api.get(`/api/projects/${projectId}/analysis/export/summary/download`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `executive_summary.md`)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// =============================================================================
// KICKOFF QUESTIONS
// =============================================================================

export const generateKickoffQuestions = (
  projectId: string,
  questionType: QuestionType,
  maxQuestions?: number
) =>
  api.post<KickoffQuestionsResult>(`/api/projects/${projectId}/kickoff/${questionType}`, null, {
    params: { max_questions: maxQuestions },
  })

export const getKickoffQuestions = (projectId: string, questionType?: QuestionType) =>
  api.get<KickoffQuestionsResult[]>(`/api/projects/${projectId}/kickoff`, {
    params: { question_type: questionType },
  })

export const downloadKickoffQuestionsCSV = async (
  projectId: string,
  questionType: QuestionType
) => {
  const response = await api.get(`/api/projects/${projectId}/kickoff/${questionType}/csv`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `kickoff_${questionType}_questions.csv`)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const deleteKickoffQuestions = (projectId: string, questionId: string) =>
  api.delete<{ success: boolean; deleted_id: string }>(
    `/api/projects/${projectId}/kickoff/${questionId}`
  )

// =============================================================================
// OUTPUTS (CSV Generation)
// =============================================================================

export const generateOutput = (projectId: string, outputType: OutputType) =>
  api.post<GeneratedOutput>(`/api/projects/${projectId}/outputs/generate`, null, {
    params: { output_type: outputType },
  })

export const generateAllOutputs = (projectId: string) =>
  api.post<OutputJobStartResponse>(`/api/projects/${projectId}/outputs/generate-all`)

export const getOutputJobStatus = (projectId: string, jobId: string) =>
  api.get<OutputJobStatusResponse>(`/api/projects/${projectId}/outputs/status/${jobId}`)

export const getOutputs = (projectId: string, outputType?: OutputType) =>
  api.get<{ outputs: GeneratedOutput[]; total: number }>(`/api/projects/${projectId}/outputs`, {
    params: { output_type: outputType },
  })

export const getOutputTypes = (projectId: string) =>
  api.get<OutputTypeInfo[]>(`/api/projects/${projectId}/outputs/types`)

export const getOutput = (outputId: string) => api.get<GeneratedOutput>(`/api/outputs/${outputId}`)

export const downloadOutput = async (outputId: string, filename: string) => {
  // Download file directly from backend
  const response = await api.get(`/api/outputs/${outputId}/download`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const getOutputContent = (outputId: string) =>
  api.get<{ content: string; row_count: number; preview_rows: string[][] }>(
    `/api/outputs/${outputId}/content`
  )

export const deleteOutput = (outputId: string) => api.delete(`/api/outputs/${outputId}`)

// =============================================================================
// SMARTSHEETS
// =============================================================================

export const connectSmartsheets = (projectId: string, data: SmartsheetConnectRequest) =>
  api.post<{ connected: boolean; smartsheet_email: string; smartsheet_name: string }>(
    `/api/projects/${projectId}/smartsheet/connect`,
    data
  )

export const getSmartsheetStatus = (projectId: string) =>
  api.get<SmartsheetConnectionStatus>(`/api/projects/${projectId}/smartsheet/status`)

export const disconnectSmartsheets = (projectId: string) =>
  api.delete(`/api/projects/${projectId}/smartsheet/disconnect`)

export const createSmartsheetWorkspace = (projectId: string) =>
  api.post<SmartsheetWorkspace>(`/api/projects/${projectId}/smartsheet/workspace`)

export const getSmartsheetWorkspace = (projectId: string) =>
  api.get<SmartsheetWorkspace>(`/api/projects/${projectId}/smartsheet/workspace`)

export const importToSmartsheets = (
  projectId: string,
  outputIds?: string[],
  createWorkspace?: boolean
) =>
  api.post<SmartsheetImportResult>(`/api/projects/${projectId}/smartsheet/import`, null, {
    params: { output_ids: outputIds, create_workspace: createWorkspace },
  })

export const getSmartsheetSheets = (projectId: string) =>
  api.get<SmartsheetSheet[]>(`/api/projects/${projectId}/smartsheet/sheets`)

export const syncSmartsheets = (projectId: string) =>
  api.post<{ workspace_id: string; sheets: SmartsheetSheet[]; sheet_count: number }>(
    `/api/projects/${projectId}/smartsheet/sync`
  )

// Delete workspace mapping from SprintForge DB (does NOT delete from Smartsheets)
export const deleteSmartsheetWorkspace = (projectId: string) =>
  api.delete(`/api/projects/${projectId}/smartsheet/workspace`)

// Delete sheet mapping from SprintForge DB (does NOT delete from Smartsheets)
export const deleteSmartsheetSheet = (projectId: string, sheetId: string) =>
  api.delete(`/api/projects/${projectId}/smartsheet/sheets/${sheetId}`)

// =============================================================================
// POST-IMPORT TASKS
// =============================================================================

export const getPostImportTasks = (projectId: string, category?: string, status?: string) =>
  api.get<PostImportTask[]>(`/api/projects/${projectId}/post-import/tasks`, {
    params: { category, status },
  })

export const getPostImportTask = (projectId: string, taskId: string) =>
  api.get<PostImportTask>(`/api/projects/${projectId}/post-import/tasks/${taskId}`)

export const updatePostImportTask = (
  projectId: string,
  taskId: string,
  status?: string,
  notes?: string
) =>
  api.patch<PostImportTask>(`/api/projects/${projectId}/post-import/tasks/${taskId}`, null, {
    params: { status, notes },
  })

export const deletePostImportTask = (projectId: string, taskId: string) =>
  api.delete(`/api/projects/${projectId}/post-import/tasks/${taskId}`)

export const getPostImportSummary = (projectId: string) =>
  api.get<PostImportSummary>(`/api/projects/${projectId}/post-import/summary`)

export const getPostImportMarkdown = (projectId: string) =>
  api.get<{ markdown: string; task_count: number; project_name: string }>(
    `/api/projects/${projectId}/post-import/markdown`
  )

export const downloadPostImportMarkdown = async (projectId: string, projectName: string) => {
  const response = await api.get(`/api/projects/${projectId}/post-import/markdown/download`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `${projectName.replace(/\s+/g, '_')}_Post_Import_Guide.md`)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const markAllTasksComplete = (projectId: string, category?: string) =>
  api.post<{ marked_complete: number; category: string | null }>(
    `/api/projects/${projectId}/post-import/tasks/mark-all-complete`,
    null,
    { params: { category } }
  )

export const deleteAllPostImportTasks = (projectId: string) =>
  api.delete(`/api/projects/${projectId}/post-import/tasks`)

// =============================================================================
// TEAMS INTEGRATION
// =============================================================================

export const getTeamsMeetings = (startDate?: string, endDate?: string, limit?: number) =>
  api.get<TeamsMeetingsResponse>('/api/teams/meetings', {
    params: { start_date: startDate, end_date: endDate, limit },
  })

export const getMeetingTranscripts = (meetingId: string, joinUrl?: string) =>
  api.get<{
    transcripts: Array<{ id: string; meeting_id: string; created_at?: string }>
    count: number
  }>(`/api/teams/meetings/${encodeURIComponent(meetingId)}/transcripts`, {
    params: { join_url: joinUrl },
  })

export const importTeamsTranscript = (projectId: string, data: TeamsTranscriptImportRequest) =>
  api.post<TeamsTranscriptImportResponse>(`/api/projects/${projectId}/teams/import`, data)

export const getProjectTeamsTranscripts = (projectId: string) =>
  api.get<{ transcripts: TeamsTranscript[]; count: number }>(
    `/api/projects/${projectId}/teams/transcripts`
  )

// =============================================================================
// PROMPTS (Geek Mode)
// =============================================================================

export interface PromptTemplate {
  id: string
  name: string
  display_name: string
  description: string
  category: string
  system_prompt: string
  user_prompt_template: string
  temperature: number
  max_tokens: number
  is_active: boolean
  version: number
  created_at: string
  updated_at: string
}

export interface PromptDefaultInfo {
  name: string
  output_type: string
  display_name: string
  filename: string
  columns: string[]
  prompt: string
  prompt_preview: string
  prompt_length: number
}

export const getPrompts = (category?: string) =>
  api.get<PromptTemplate[]>('/api/prompts', {
    params: { category },
  })

export const getPrompt = (promptName: string) =>
  api.get<PromptTemplate>(`/api/prompts/${promptName}`)

export const updatePrompt = (
  promptName: string,
  updates: {
    display_name?: string
    description?: string
    system_prompt?: string
    user_prompt_template?: string
    temperature?: number
    max_tokens?: number
    is_active?: boolean
  }
) => api.put<PromptTemplate>(`/api/prompts/${promptName}`, updates)

export const createPrompt = (prompt: {
  name: string
  display_name: string
  description?: string
  category: string
  system_prompt?: string
  user_prompt_template: string
  temperature?: number
  max_tokens?: number
}) => api.post<PromptTemplate>('/api/prompts', prompt)

export const getPromptDefaults = () => api.get<PromptDefaultInfo[]>('/api/prompts/defaults/list')

export const getPromptDefault = (outputType: string) =>
  api.get<PromptDefaultInfo>(`/api/prompts/defaults/${outputType}`)

export default api
